import { CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { Button, Checkbox } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import DaypartTableText from './components/DaypartTableText';
import { ALL_HOURS, ALL_HOURS_NAMES, ALL_WEEK, CELL_SIZE_CLASSNAME, DAYPART_PRESETS, DAYS_OF_WEEK } from './constants';

const LineItemFormDaypartSection = () => {
  const { setValue, control } = useFormContext();

  const watchDaypart = useWatch({ control, name: 'daypart' });

  const isAllSelected =
    !!Object.values(watchDaypart).length && Object.values(watchDaypart).every(day => day.length === ALL_HOURS.length);

  const handleSelect = (selectedDay, selectedHour) => {
    setValue(
      'daypart',
      {
        ...watchDaypart,
        [selectedDay]: watchDaypart[selectedDay]?.includes(selectedHour)
          ? watchDaypart[selectedDay].filter(h => h !== selectedHour)
          : [...(watchDaypart[selectedDay] || []), selectedHour],
      },
      { shouldDirty: true },
    );
  };

  const handleDaySelect = selectedDay => {
    setValue(
      'daypart',
      {
        ...watchDaypart,
        [selectedDay]: watchDaypart[selectedDay]?.length === ALL_HOURS.length ? [] : ALL_HOURS,
      },
      { shouldDirty: true },
    );
  };

  const areAllDaysHoursSelected = hours =>
    !!Object.values(watchDaypart)?.length &&
    Object.values(watchDaypart).every(day => hours.every(hour => day.includes(hour)));

  const handleHoursSelect = selectedHours => {
    setValue(
      'daypart',
      areAllDaysHoursSelected(selectedHours)
        ? Object.entries(watchDaypart).reduce((acc, [day, hours]) => {
            acc[day] = hours.filter(hour => !selectedHours.includes(hour));
            return acc;
          }, {})
        : DAYS_OF_WEEK.reduce((acc, day) => {
            acc[day] = [...new Set([...(watchDaypart[day] || []), ...selectedHours])];
            return acc;
          }, {}),
      { shouldDirty: true },
    );
  };

  const handleSelectAll = () => {
    setValue('daypart', isAllSelected ? {} : ALL_WEEK, { shouldDirty: true });
  };

  const isPresetActive = presetHours => {
    return DAYS_OF_WEEK.every(day => watchDaypart[day] && presetHours.every(hour => watchDaypart[day].includes(hour)));
  };

  const renderTable = () => {
    const rows = [];

    const firstRow = [
      <th style={{ padding: 0 }} key='selectAll' className={CELL_SIZE_CLASSNAME}>
        <DaypartTableText value={'Select All'} direction={'col'} />
        <Checkbox
          icon={<RadioButtonUnchecked />}
          checkedIcon={<CheckCircle />}
          checked={isAllSelected}
          onChange={handleSelectAll}
        />
      </th>,
    ];
    ALL_HOURS.forEach((hour, colIndex) => {
      firstRow.push(
        <th style={{ padding: 0 }} key={`time${colIndex}`} className={CELL_SIZE_CLASSNAME}>
          <div className=''>
            <DaypartTableText value={ALL_HOURS_NAMES[colIndex]} direction={'col'} />
            <Checkbox
              icon={<RadioButtonUnchecked />}
              checkedIcon={<CheckCircle />}
              checked={areAllDaysHoursSelected([hour])}
              onChange={() => {
                handleHoursSelect([hour]);
              }}
            />
          </div>
        </th>,
      );
    });
    rows.push(<tr key='row0'>{firstRow}</tr>);

    DAYS_OF_WEEK.forEach((day, rowIndex) => {
      const row = [
        <th style={{ padding: 0 }} key={`day${rowIndex}`} className={`relative ${CELL_SIZE_CLASSNAME}`}>
          <span className='capitalize text-[#737373] font-[Manrope] text-sm font-semibold break-words absolute bottom-[12px] left-[-24px]'>
            {day}
          </span>
          <Checkbox
            icon={<RadioButtonUnchecked />}
            checkedIcon={<CheckCircle />}
            checked={watchDaypart[day]?.length === ALL_HOURS.length}
            onChange={() => handleDaySelect(day)}
          />
        </th>,
      ];
      ALL_HOURS.forEach((hour, colIndex) => {
        const isLeftTop = colIndex === 0 && rowIndex === 0 ? 'rounded-tl' : '';
        const isLeftBottom = colIndex === 0 && rowIndex === DAYS_OF_WEEK.length - 1 ? 'rounded-bl' : '';
        const isRightBottom =
          colIndex === ALL_HOURS.length - 1 && rowIndex === DAYS_OF_WEEK.length - 1 ? 'rounded-br' : '';
        const isRightTop = colIndex === ALL_HOURS.length - 1 && rowIndex === 0 ? 'rounded-tr' : '';
        const cellXStyle = colIndex === 0 ? 'border-l' : 'border-l-0';
        const cellYStyle = rowIndex === 0 ? 'border-t' : 'border-t-0';

        const cornerBorderStyle = `${isLeftTop} ${isLeftBottom} ${isRightTop} ${isRightBottom} ${cellXStyle} ${cellYStyle}`;

        row.push(
          <td
            style={{ padding: 0 }}
            key={`cell${rowIndex}:${colIndex}`}
            className={`text-center align-middle ${CELL_SIZE_CLASSNAME}`}
          >
            <div className={`border border-[#E6E6E6] border-solid ${cornerBorderStyle}`}>
              <Checkbox
                icon={<RadioButtonUnchecked />}
                checkedIcon={<CheckCircle />}
                checked={!!watchDaypart[day]?.includes(hour)}
                onChange={() => handleSelect(day, hour)}
              />
            </div>
          </td>,
        );
      });
      rows.push(<tr key={`row${rowIndex + 1}`}>{row}</tr>);
    });

    return rows;
  };

  return (
    <div className='min-w-[900px]'>
      <span className='text-lg text-[#1E1E1E] font-semibold font-[Plus Jakarta Sans]'>Daypart</span>
      <div className='flex justify-center'>
        <table className='relative ml-5 mt-5 table-fixed w-full'>
          <tbody style={{ paddingBottom: 3 }}>{renderTable()}</tbody>
        </table>
      </div>
      <div className='mt-10'>
        <span className='text-lg text-[#1E1E1E] font-semibold font-[Plus Jakarta Sans]'>Daypart presets</span>
        <div className='mt-2.5 flex flex-wrap gap-x-3 gap-y-3.5'>
          {DAYPART_PRESETS.map(preset => (
            <Button
              key={preset.id}
              sx={{
                padding: '10px 16px',
                '&.MuiButton-contained': {
                  backgroundColor: '#1b43320a',
                  border: '1px solid #1B4332',
                  color: '#1B4332',
                },
              }}
              variant={isPresetActive(preset.presetHours) ? 'contained' : 'outlined'}
              onClick={() => handleHoursSelect(preset.presetHours)}
            >
              {preset.title}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LineItemFormDaypartSection;
